const MONTH_ORDER = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

function isMonthlyStructure(yearData) {
  return Object.keys(yearData || {}).some((k) => MONTH_ORDER.includes(k));
}

/**
 * Level-1 metric/datapoint IDs and level-1 equation IDs from widget configuration.
 * Used to exclude level-2 metrics from stacked bar carousel slides.
 */
export function getLevel1IdsFromConfigRows(configRows) {
  const metricIds = new Set();
  const equationIds = new Set();
  if (!Array.isArray(configRows)) {
    return { metricIds, equationIds };
  }
  for (const row of configRows) {
    if (!row) continue;
    if (row.level1?.itemType === 'equation' && row.level1?.eqExpressionWithId != null) {
      equationIds.add(String(row.level1?.eqExpressionWithId));
    } else if (row.level1?.itemType === 'metric' && row.level1?.id != null) {
      metricIds.add(String(row.level1.id));
    }
  }
  return { metricIds, equationIds };
}

/**
 * Builds carousel items for ESG stacked bar charts: one slide per metric or level-1 equation
 * present in dataViewData. Previously only metrics were collected, so level-1 equation rows
 * never appeared when any metric existed in the response.
 *
 * When `configRows` is a non-empty array, only metrics and equations that are configured as
 * level 1 are included (level-2 breakdown metrics are omitted).
 *
 * @param {object} dataViewData
 * @param {object[]} [configRows] - optional; when set, filter to level-1 IDs only
 */
export function collectStackedBarCarouselItemsFromDataView(dataViewData, configRows) {
  if (!dataViewData || typeof dataViewData !== 'object') return [];

  const filterByConfig = Array.isArray(configRows) && configRows.length > 0;
  const { metricIds: level1MetricIds, equationIds: level1EquationIds } = filterByConfig
    ? getLevel1IdsFromConfigRows(configRows)
    : { metricIds: null, equationIds: null };

  const metricSeen = new Map();
  const equationSeen = new Map();
  const years = Object.keys(dataViewData)
    .filter((y) => /^\d{4}$/.test(y))
    .sort((a, b) => parseInt(a, 10) - parseInt(b, 10));

  for (const year of years) {
    const yearData = dataViewData[year];
    if (!yearData || typeof yearData !== 'object') continue;

    const periods = isMonthlyStructure(yearData)
      ? MONTH_ORDER.filter((p) => yearData[p])
      : Object.keys(yearData);

    for (const period of periods) {
      const periodData = yearData[period];
      if (!periodData || typeof periodData !== 'object') continue;

      const metrics = periodData.metrics;
      if (Array.isArray(metrics)) {
        for (const m of metrics) {
          if (m?.metricId == null || metricSeen.has(m.metricId)) continue;
          if (filterByConfig && !level1MetricIds.has(String(m.metricId))) continue;
          const metricConfigRow = Array.isArray(configRows)
            ? configRows.find(
              (row) =>
                row?.level1?.itemType === 'metric' &&
                String(row?.level1?.id) === String(m.metricId)
            )
            : null;
          const metricSortOrderRaw = Number(metricConfigRow?.sortOrder);
          metricSeen.set(m.metricId, {
            itemKind: 'metric',
            metricId: m.metricId,
            metricName: m.metricName || `Metric ${m.metricId}`,
            sortOrder: Number.isFinite(metricSortOrderRaw) ? metricSortOrderRaw : 0,
          });
        }
      }

      const equations = periodData.equations;
      if (Array.isArray(equations)) {
        for (const eq of equations) {
          const eqId = eq?.equation ?? eq?.expressionWithId ?? eq?.id;
          if (eqId == null || equationSeen.has(eqId)) continue;
          if (filterByConfig && !level1EquationIds.has(String(eqId))) continue;
          const equationConfigRow = Array.isArray(configRows)
            ? configRows.find(
              (row) =>
                row?.level1?.itemType === 'equation' &&
                String(row?.level1?.eqExpressionWithId) === String(eqId)
            )
            : null;
          const equationSortOrderRaw = Number(equationConfigRow?.sortOrder);
          equationSeen.set(eqId, {
            itemKind: 'equation',
            equationId: eqId,
            equationName:
              eq.equationName || eq.name || eq.label || eq.metricName || `Equation ${eqId}`,
            sortOrder: Number.isFinite(equationSortOrderRaw) ? equationSortOrderRaw : 0,
          });
        }
      }
    }
  }

  const items = [...metricSeen.values(), ...equationSeen.values()];
  return items.sort((a, b) => {
    const o = (a.sortOrder || 0) - (b.sortOrder || 0);
    if (o !== 0) return o;
    const na = a.itemKind === 'metric' ? a.metricName : a.equationName;
    const nb = b.itemKind === 'metric' ? b.metricName : b.equationName;
    return String(na).localeCompare(String(nb));
  });
}

/**
 * Level-1 row label from widget configuration (matches pre-carousel behavior: user-defined row name).
 */
function resolveLevelRowNameFromConfig(item, configRows) {
  if (!item || !Array.isArray(configRows)) return null;
  for (const row of configRows) {
    if (!row) continue;
    if (item.itemKind === 'equation') {
      const equationIds = [
        row.level1?.eqExpressionWithId,
      ].filter((v) => v != null && v !== '');
      if (equationIds.some((id) => String(id) === String(item.equationId))) {
        return row.name?.trim() ? row.name : null;
      }
    }
    if (
      item.itemKind === 'metric' &&
      row.level1?.itemType === 'metric' &&
      row.level1?.id != null &&
      String(row.level1.id) === String(item.metricId)
    ) {
      return row.name?.trim() ? row.name : null;
    }
  }
  return null;
}

/**
 * @param {object} item - carousel item from collectStackedBarCarouselItemsFromDataView
 * @param {object[]} [configRows] - widget level rows; when set, chart title uses row.name like before
 */
export function getConfigRowForStackedBarCarouselItem(item, configRows) {
  if (!item || !item.itemKind) return null;
  const displayName = resolveLevelRowNameFromConfig(item, configRows) ||
    (item.itemKind === 'equation' ? item.equationName : item.metricName);

  if (item.itemKind === 'equation') {
    return {
      id: `equation-${item.equationId}`,
      name: displayName,
      type: 'equation',
      useLevel1Equation: true,
      level1Equation: {
        expressionWithId: item.equationId,
      },
    };
  }
  if (item.itemKind === 'metric') {
    return {
      id: `metric-${item.metricId}`,
      name: displayName,
      type: 'metric',
      level1: {
        id: item.metricId,
        name: item.metricName,
        itemType: 'metric',
        metricName: item.metricName,
      },
    };
  }
  return null;
}
